import React, { memo, useState, useCallback, useMemo } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { useGHL } from 'Context/GHL';

import { ReferralDashboardContainer } from '../ReferralDashboard';
import { LeadFormsContainer } from '../LeadForms';
import { ReportProfiles } from '../LeadForms/ReportProfiles';
import { QuickBooksContainer } from '../QuickBooks';
import { JobConfigContainer } from '../JobConfig';
import { JobCostingContainer } from '../JobCosting';
import { BulkExportContainer } from '../BulkExport';
import { OpportunitySearchContainer } from '../OpportunitySearch';
import { CampaignsContainer } from '../Campaigns';
import { ContactListsContainer } from '../ContactLists';
import { CustomMenuLinksContainer } from '../CustomMenuLinks';
import { GHLDocuments } from '../GHLDocuments';

import classes from './ghlMain.module.css';

interface TabConfig {
  id: string;
  label: string;
  adminOnly?: boolean;
}

const allTabs: TabConfig[] = [
  { id: 'referrals', label: 'Referral Dashboard' },
  { id: 'lead-forms', label: 'Lead Forms' },
  { id: 'report-profiles', label: 'Report Profiles', adminOnly: true },
  { id: 'quickbooks', label: 'QuickBooks', adminOnly: true },
  { id: 'job-config', label: 'Job Configuration', adminOnly: true },
  { id: 'job-costing', label: 'Job Costing' },
  { id: 'data-export', label: 'Data Export', adminOnly: true },
  { id: 'opp-search', label: 'Search' },
  { id: 'campaigns', label: 'Campaigns', adminOnly: true },
  { id: 'contact-lists', label: 'Contact Lists', adminOnly: true },
  { id: 'custom-menu-links', label: 'Menu Links', adminOnly: true },
  { id: 'documents', label: 'PDF Forms' },
  // Future tabs:
  // { id: 'dashboard', label: 'Dashboard' },
];

function GHLMainContainer() {
  const {
    isAuthenticated,
    userName,
    role,
    isAdmin,
    locationId,
    ssoToken,
    companyId,
    error: ghlError,
    errorType: ghlErrorType,
    isLoading: ghlLoading,
  } = useGHL();
  const initialTab = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'referrals';
  }, []);
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  // Filter tabs based on admin status
  const tabs = useMemo(
    () => allTabs.filter((tab) => !tab.adminOnly || isAdmin),
    [isAdmin]
  );

  const handleTabClick = useCallback((tabId: string) => {
    setActiveTab(tabId);
  }, []);

  // Handle GHL loading state
  if (ghlLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.centeredCard}>
          <div className={classes.logoSection}>
            <div className={classes.logoIcon}>🚀</div>
            <h1 className={classes.logoText}>RocketPlan</h1>
          </div>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className={classes.statusText}>Connecting...</p>
        </div>
      </div>
    );
  }

  // Handle "Location not linked to company" error - show setup required screen
  if (ghlErrorType === 'LOCATION_NOT_LINKED') {
    return (
      <div className={classes.container}>
        <div className={classes.centeredCard}>
          <div className={classes.logoSection}>
            <div className={classes.logoIcon}>⚙️</div>
            <h1 className={classes.logoText}>Setup Required</h1>
          </div>
          <p className={classes.welcomeText}>
            This location needs to be configured
          </p>
          <p className={classes.instructionText}>
            Your GoHighLevel location has not been linked to a RocketPlan company yet.
            Please contact your administrator or RocketPlan support to complete the setup.
          </p>
          {locationId && (
            <div className={classes.locationInfo}>
              <p className={classes.locationLabel}>Location ID:</p>
              <code className={classes.locationCode}>{locationId}</code>
            </div>
          )}
          <div className={classes.supportInfo}>
            <p>Need help? Contact support with your location ID above.</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle GHL error or not authenticated state - show friendly welcome screen
  if (ghlError || !isAuthenticated) {
    return (
      <div className={classes.container}>
        <div className={classes.centeredCard}>
          <div className={classes.logoSection}>
            <div className={classes.logoIcon}>🚀</div>
            <h1 className={classes.logoText}>RocketPlan</h1>
          </div>
          <p className={classes.welcomeText}>
            Welcome to RocketPlan for RocketCRM
          </p>
          <p className={classes.instructionText}>
            To get started, please access this page from within your RocketCRM account
            where the RocketPlan app is installed.
          </p>
          <div className={classes.featureList}>
            <div className={classes.featureItem}>
              <span className={classes.featureIcon}>📊</span>
              <span>Track referral performance</span>
            </div>
            <div className={classes.featureItem}>
              <span className={classes.featureIcon}>💰</span>
              <span>Monitor opportunity values</span>
            </div>
            <div className={classes.featureItem}>
              <span className={classes.featureIcon}>🤝</span>
              <span>Manage partner relationships</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h1>RocketPlan</h1>
        <p className={classes.subtitle}>
          {'Welcome, '}
          {userName || 'User'}
          {role === 'admin' && <span className={classes.badge}>Admin</span>}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className={classes.tabNav}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`${classes.tab} ${activeTab === tab.id ? classes.tabActive : ''}`}
            onClick={() => handleTabClick(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={classes.tabContent}>
        {activeTab === 'referrals' && <ReferralDashboardContainer />}
        {activeTab === 'lead-forms' && <LeadFormsContainer />}
        {activeTab === 'report-profiles' && isAdmin && locationId && ssoToken && (
          <ReportProfiles locationId={locationId} ssoToken={ssoToken} />
        )}
        {activeTab === 'quickbooks' && isAdmin && locationId && ssoToken && (
          <QuickBooksContainer locationId={locationId} ssoToken={ssoToken} isAdmin={isAdmin} />
        )}
        {activeTab === 'job-config' && isAdmin && locationId && ssoToken && (
          <JobConfigContainer locationId={locationId} ssoToken={ssoToken} />
        )}
        {activeTab === 'job-costing' && locationId && ssoToken && (
          <JobCostingContainer locationId={locationId} ssoToken={ssoToken} />
        )}
        {activeTab === 'data-export' && isAdmin && <BulkExportContainer />}
        {activeTab === 'documents' && <GHLDocuments />}
        {activeTab === 'opp-search' && locationId && ssoToken && (
          <OpportunitySearchContainer locationId={locationId} ssoToken={ssoToken} />
        )}
        {activeTab === 'campaigns' && isAdmin && ssoToken && (
          <CampaignsContainer ssoToken={ssoToken} companyId={companyId} />
        )}
        {activeTab === 'contact-lists' && isAdmin && ssoToken && (
          <ContactListsContainer ssoToken={ssoToken} companyId={companyId} />
        )}
        {activeTab === 'custom-menu-links' && isAdmin && ssoToken && locationId && (
          <CustomMenuLinksContainer ssoToken={ssoToken} companyId={companyId} locationId={locationId} />
        )}
        {/* Future tab content:
        {activeTab === 'dashboard' && <GHLDashboard />}
        */}
      </div>
    </div>
  );
}

const GHLMainContainerMemo = memo(GHLMainContainer, areEqual);

export { GHLMainContainerMemo as GHLMainContainer };
